import { trim } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Button, { TYPES } from 'components/Button';
import Checkbox, { SIZES } from 'components/Checkbox';
import { SecondaryHeading } from 'components/Headings';
import Input from 'components/Input';
import Label from 'components/Label';
import LoadingState from 'components/LoadingState';
import Paragraph from 'components/Paragraph';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';
import Validator from 'utils/Validator';

import ProductTile from './ProductTile';

const Form = class extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isSigningUpForNewsletterToo: true,
      emailAddress: '',
    };
  }

  get trimmedEmail() {
    return trim(this.state.emailAddress);
  }

  get isValidForm() {
    return Validator.isEmail(this.trimmedEmail);
  }

  handleSubmit = e => {
    const { signUpForProductUpdates, signUpForNewsletter, variant } = this.props;
    const { isSigningUpForNewsletterToo } = this.state;

    const promises = [
      signUpForProductUpdates({
        variantSku: variant.sku,
        emailAddress: this.trimmedEmail,
        origination: 'productUpdatesForm',
        pdpSignupFlow: 'backInStock',
      }),
    ];

    if (isSigningUpForNewsletterToo) {
      promises.push(
        signUpForNewsletter({
          emailAddress: this.trimmedEmail,
          origination: 'productUpdatesForm',
          pdpSignupFlow: 'backInStock',
        })
      );
    }

    e.preventDefault();

    return Promise.all(promises);
  };

  handleEmailAddressChange = e => {
    this.setState({ emailAddress: e.target.value });
  };

  handleNewsletterChange = e => {
    this.setState({ isSigningUpForNewsletterToo: e.target.checked });
  };

  render() {
    const { product, variant, isLoading } = this.props;

    return (
      <form className="Form" onSubmit={this.handleSubmit}>
        <div className="Form__header">
          <SecondaryHeading>{I18n.localize('pdp.updateForm.header')}</SecondaryHeading>
        </div>
        <div className="Form__subheader">
          <Paragraph>{I18n.localize('pdp.updateForm.subheader')}</Paragraph>
        </div>
        <ProductTile product={product} variant={variant} />
        <div className="Form__inputs">
          <Label htmlFor="Form__input">Email Address</Label>
          <Input
            id="Form__input"
            className="Form__input"
            onChange={this.handleEmailAddressChange}
            value={this.state.emailAddress}
            placeholder={I18n.localize('pdp.updateForm.enterYourEmail')}
            ariaLabel={I18n.localize('pdp.updateForm.enterYourEmail')}
          />
          <Checkbox
            className="Form__option-checkbox"
            isChecked={this.state.isSigningUpForNewsletterToo}
            onChange={this.handleNewsletterChange}
            ariaLabel={I18n.localize('pdp.updateForm.signUpForNewsletter')}
            size={SIZES.SMALL}
          >
            <span className="Form__option-label">
              {I18n.localize('pdp.updateForm.signUpForNewsletter')}
            </span>
          </Checkbox>
          <Button
            type={TYPES.DEFAULT}
            isDisabled={!this.isValidForm}
            isFullWidth
            isUppercase
            isSubmit
          >
            {I18n.localize('pdp.updateForm.cta')}
          </Button>
        </div>
        {isLoading && <LoadingState className="Form__loader" />}
        <style jsx>
          {`
            .Form__header {
              text-align: center;
              margin-bottom: ${Theme.get('spacing') * 3}px;]
            }

            .Form__subheader {
              text-align: center;
              max-width: ${Theme.get('container-width-min-px')};
              margin: 0 auto;
            }

            .Form__inputs {
              max-width: ${Theme.get('container-width-min-px')};
              margin: 0 auto;
            }
          `}
        </style>
        <style jsx global>
          {`
            .Form__input {
              margin-bottom: ${Theme.get('spacing') * 3}px;
            }

            .Form__option-checkbox {
              margin-bottom: ${Theme.get('spacing') * 5}px;
            }
          `}
        </style>
      </form>
    );
  }
};

Form.propTypes = {
  product: PropTypes.shape({}).isRequired,
  variant: PropTypes.shape({
    sku: PropTypes.string.isRequired,
  }).isRequired,
  isLoading: PropTypes.bool,
  signUpForProductUpdates: PropTypes.func.isRequired,
  signUpForNewsletter: PropTypes.func.isRequired,
};

Form.defaultProps = {
  isLoading: false,
};

export default Form;
